// =========================
// FEED VIEW
// =========================
import App from './../../App';
import { html, render } from 'lit-html';
import Auth from './../../Auth';
import Utils from './../../Utils';

// This is the FeedView class that handles the feed page
// It includes methods for fetching posts, rendering the page, and handling user interactions
class FeedView {
  constructor() {
    this.posts = [];
    this.displayedPosts = [];
    this.postBatchSize = 5;
    this.user = Auth.currentUser;
    this.observer = null;
    this.loading = true;
    this.discoverEvents = [];
    this.postCreatedHandler = async () => {
      await this.getPosts();

      this.posts = await Promise.all(
        this.posts.map(async (post) => {
          const user = post.userId;
          if (user?.role === 'supplier' && !user.businessName) {
            const supplier = await Utils.getSupplierDetails(
              user._id || user.id
            );
            if (supplier?.businessName) {
              user.businessName = supplier.businessName;
            }
          }

          user.displayName = Utils.getDisplayName(user);
          user.profileImage = Utils.getProfileImage(user);

          post.userId = user;
          return post;
        })
      );

      this.displayedPosts = this.posts.slice(0, this.postBatchSize);
      this.render();
    };
  }

  // This method initializes the view
  // It sets the document title, fetches the current user, and retrieves posts
  async init() {
    document.title = 'PopIn - Feed';
    window.addEventListener('postCreated', this.postCreatedHandler);

    this.loading = true;
    this.render();

    try {
      const res = await fetch(`${App.apiBase}/user/${Auth.currentUser.id}`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('accessToken')}`,
        },
      });

      if (!res.ok) throw new Error('Failed to fetch current user');
      this.user = await res.json();

      if (this.user.role === 'supplier' && !this.user.businessName) {
        const supplier = await Utils.getSupplierDetails(
          this.user._id || this.user.id
        );
        if (supplier?.businessName) {
          this.user.businessName = supplier.businessName;
        }
      }

      this.user.displayName = Utils.getDisplayName(this.user);
      this.user.profileImage = Utils.getProfileImage(this.user);
    } catch (err) {
      console.error('Error fetching user in feed view:', err);
      this.user = Auth.currentUser;

      if (this.user.role === 'supplier' && !this.user.businessName) {
        try {
          const supplier = await Utils.getSupplierDetails(
            this.user._id || this.user.id
          );
          if (supplier?.businessName) {
            this.user.businessName = supplier.businessName;
          }
        } catch (err) {
          console.warn('Fallback: Failed to fetch supplier data:', err);
        }
      }

      this.user.displayName = Utils.getDisplayName(this.user);
      this.user.profileImage = Utils.getProfileImage(this.user);
    }

    await this.getPosts();

    this.posts = await Promise.all(
      this.posts.map(async (post) => {
        const user = post.userId;

        if (user?.role === 'supplier' && !user.businessName) {
          const supplier = await Utils.getSupplierDetails(user._id || user.id);
          if (supplier?.businessName) {
            user.businessName = supplier.businessName;
          }
        }

        user.displayName = Utils.getDisplayName(user);
        user.profileImage = Utils.getProfileImage(user);

        post.userId = user;
        return post;
      })
    );

    this.displayedPosts = this.posts.slice(0, this.postBatchSize);
    await this.getDiscoverEvents();
    this.loading = false;

    this.render();
    Utils.pageIntroAnim();

    await new Promise((r) => setTimeout(r, 100));

    const shouldStartGuide =
      localStorage.getItem('startGuideOnLoad') === 'true';
    if (!this.user.hasSeenGuide || shouldStartGuide) {
      document.querySelector('popin-guide')?.startGuide();
      localStorage.removeItem('startGuideOnLoad');
    }
  }

  // This method fetches posts from the API
  // It sorts them by creation date and updates the posts array
  async getPosts() {
    try {
      const res = await fetch(`${App.apiBase}/post`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('accessToken')}`,
        },
      });
      const data = await res.json();
      this.posts = data.sort(
        (a, b) => new Date(b.createdAt) - new Date(a.createdAt)
      );
    } catch (err) {
      console.error('Error fetching posts', err);
    }
  }

  // This method sets up an Intersection Observer to load more posts when the last post is visible
  setupObserver() {
    if (this.observer) this.observer.disconnect();

    const posts = document.querySelectorAll('popin-post');
    const lastPost = posts[posts.length - 1];
    if (!lastPost) return;

    this.observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          const currentLength = this.displayedPosts.length;
          if (currentLength < this.posts.length) {
            this.displayedPosts = this.posts.slice(
              0,
              currentLength + this.postBatchSize
            );
            this.render();
          }
        }
      },
      {
        root: null,
        rootMargin: '0px',
        threshold: 1.0,
      }
    );

    this.observer.observe(lastPost);
  }

  // This method fetches discover events from the API
  async getDiscoverEvents() {
    try {
      const res = await fetch(`${App.apiBase}/event?limit=3`);
      if (!res.ok) throw new Error('Failed to fetch discover events');

      const data = await res.json();
      this.discoverEvents = data;
    } catch (err) {
      console.error('Error fetching discover events:', err);
    }
  }

  render() {
    const template = html`
      <div class="layout">
        <popin-side-nav></popin-side-nav>
        <div class="main-area">
          <popin-top-bar .user=${this.user}></popin-top-bar>
          <div class="main-content feed-page">
            ${this.loading
              ? html`
                  <sl-spinner
                    style="margin: 3rem auto; display: block;"
                  ></sl-spinner>
                `
              : html`
                  <popin-poster .user=${this.user}></popin-poster>
                  <div id="feed-wrapper">
                    <div id="feed-posts">
                      ${this.displayedPosts.map(
                        (post) => html`<popin-post .post=${post}></popin-post>`
                      )}
                    </div>
                    <div id="feed-discover">
                      <h2>Discover Events</h2>
                      <div class="discover-events-grid">
                        ${this.discoverEvents.map(
                          (event) =>
                            html`<popin-event-card
                              .event=${event}
                            ></popin-event-card>`
                        )}
                      </div>
                    </div>
                  </div>
                `}
          </div>
        </div>
      </div>
      <popin-guide></popin-guide>
    `;
    render(template, App.rootEl);
    if (!this.loading) this.setupObserver();
  }
}

export default new FeedView();
